import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { ApplicationPrivateRoutesEnum } from '../../models/enums/application-routes.enum'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { LogService } from '../../services/log.service'
import * as FeatureEPActions from './feature-ep.actions'
import { FeatureEPService } from './feature-ep.service'

@Injectable()
export class FeatureEPEffects {
   private _cn = this.constructor.name
   private _router = inject(Router)
   private _actions$ = inject(Actions)
   private _logService = inject(LogService)
   private _service = inject(FeatureEPService)
   private _errorHandlerService = inject(AppErrorHandlerService)

   actionCreateProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureEPActions.createProject),
         switchMap((action) =>
            this._service.createProject(action.body).pipe(
               tap((res) => this._logService.debug(this._cn, 'create project effect', 'response:', res)),
               map((res) => FeatureEPActions.createProjectSuccess({ id: res?.id, status: res?.statusId })),
               catchError((error: HttpErrorResponse) => {
                  return of(FeatureEPActions.createProjectFailure({ error: error }))
               })
            )
         )
      )
   )

   actionCreateDraftProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureEPActions.createDraft),
         switchMap((action) =>
            this._service.createDraft(action.body).pipe(
               tap((res) => this._logService.debug(this._cn, 'create draft effect', 'response:', res)),
               map((res) => FeatureEPActions.createDraftSuccess({ id: res?.id, status: res?.statusId })),
               catchError((error: HttpErrorResponse) => {
                  return of(FeatureEPActions.createDraftFailure({ error: error }))
               })
            )
         )
      )
   )

   actionEditProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureEPActions.editProject),
         switchMap((action) =>
            this._service.editProject(action.id, action.body).pipe(
               tap((res) => this._logService.debug(this._cn, 'edit project effect', 'response:', res)),
               map((res: { id: number; statusId: number }) => FeatureEPActions.editProjectSuccess({ id: res?.id, status: res?.statusId })),
               catchError((error: HttpErrorResponse) => {
                  return of(FeatureEPActions.editProjectFailure({ error: error }))
               })
            )
         )
      )
   )

   actionEditDraftProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(FeatureEPActions.editDraft),
         switchMap((action) =>
            this._service.editDraft(action.id, action.body).pipe(
               tap((res) => this._logService.debug(this._cn, 'edit draft effect', 'response:', res)),
               map((res: { id: number; statusId: number }) => FeatureEPActions.editDraftSuccess({ id: res?.id, status: res?.statusId })),
               catchError((error: HttpErrorResponse) => {
                  return of(FeatureEPActions.editDraftFailure({ error: error }))
               })
            )
         )
      )
   )

   private _actionsForErrorNotificationMessage = [
      FeatureEPActions.createProjectFailure,
      FeatureEPActions.editProjectFailure,
      FeatureEPActions.createDraftFailure,
      FeatureEPActions.editDraftFailure,
   ]

   listenAllFailureAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForErrorNotificationMessage),
            map((errorResponse) => this._errorHandlerService.handleError(errorResponse.error))
         ),
      { dispatch: false }
   )

   private _actionsForSwitch = [
      FeatureEPActions.createProjectSuccess,
      FeatureEPActions.editProjectSuccess,
      FeatureEPActions.createDraftSuccess,
      FeatureEPActions.editDraftSuccess,
   ]

   switchToMYProject$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSwitch),
            map((res) => this._router.navigate([ApplicationPrivateRoutesEnum.MAIN_PAGE + `/project-details/${res.id}/${res.status}`]))
         ),
      { dispatch: false }
   )
}
